import type {AfriexPaymentMethodStatus} from '@afriex/core/domain/enums';
import {PaymentMethodStatus} from '@afriex/core/domain/types';

import type {AfriexBankAccount} from '@/types';

import {capitalizeWords} from './strings';

export const formatSelectOptionItem = (item: string) => {
  let formattedItem = item.replace(/_/g, ' ');
  formattedItem =
    formattedItem.length === 2 || formattedItem.length === 3
      ? formattedItem.toUpperCase()
      : formattedItem.toLowerCase();

  return formattedItem.replace(/\b\w/g, (c) => c.toUpperCase());
};

export const isPlainObject = (val: unknown): val is Record<string, unknown> => {
  return typeof val === 'object' && val !== null && !Array.isArray(val);
};

export const formatBusinessAddress = (address: unknown) => {
  if (typeof address === 'string') {
    return address;
  }
  if (isPlainObject(address)) {
    return `${String(address?.street)}, ${String(address?.town)}, ${String(address?.state)}, ${String(address?.country)}`;
  }
  return '';
};

// Virtual accounts are deposit rails; providers that mint a fresh, time-limited
// account per payment set `expiresInMinutes` on the account info. A stable
// (reserved) account has no expiry. This mirrors how the providers persist them.
export const getAccountType = (info: AfriexBankAccount): string =>
  info?.expiresInMinutes ? 'Dynamic' : 'Static';

// Accepts both of core's payment-method status enums. They are two names for
// one set of string values: `PaymentMethodStatus` (types/AfriexPaymentMethod.ts)
// is what retail payment methods carry, `AfriexPaymentMethodStatus`
// (enums/AfriexPaymentMethodStatus.ts) is what BusinessPaymentMethod.status
// carries. Collapsing the two is a core change, tracked separately.
export const statusColorScheme = (
  status?: PaymentMethodStatus | AfriexPaymentMethodStatus,
): string => {
  switch (status) {
    case PaymentMethodStatus.ACTIVE:
      return 'successful';
    case PaymentMethodStatus.PENDING:
      return 'pending';
    case PaymentMethodStatus.DELETED:
    case PaymentMethodStatus.EXPIRED:
    case PaymentMethodStatus.BLOCKED:
      return 'failed';
    default:
      return 'gray';
  }
};

export const formatProvider = (processor?: string): string =>
  processor ? capitalizeWords(processor.replace(/_/g, ' ')) : '—';
